import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ChevronDown, ChevronRight, ImagePlus, X } from "lucide-react";
import { emptyRole, emptyPrev, emptyEdu, emptyCert, readFileAsDataUrl } from "@/lib/cv";
import { toast } from "sonner";

const Section = ({ title, children, defaultOpen = true, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#e2ddd3] rounded-lg bg-white overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[#faf8f5]"
      >
        <span className="overline text-[#0f1c3f]">{title}</span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 space-y-4">{children}</div>}
    </div>
  );
};

const Field = ({ label, ...props }) => (
  <div>
    <Label className="text-[11px] font-mono-tram tracking-[0.2em] uppercase text-[#7a6b5d]">
      {label}
    </Label>
    <Input className="mt-1 bg-white border-[#cfc8ba]" {...props} />
  </div>
);

const TArea = ({ label, ...props }) => (
  <div>
    <Label className="text-[11px] font-mono-tram tracking-[0.2em] uppercase text-[#7a6b5d]">
      {label}
    </Label>
    <Textarea className="mt-1 bg-white border-[#cfc8ba] min-h-[110px]" {...props} />
  </div>
);

const PhotoField = ({ cv, setCv }) => {
  const inp = useRef(null);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Photo must be under 3MB");
    if (!/^image\//.test(f.type)) return toast.error("Please choose an image file");
    const dataUrl = await readFileAsDataUrl(f);
    setCv((c) => ({ ...c, photo_data_url: dataUrl }));
  };
  return (
    <div className="flex items-center gap-4">
      <div
        className="w-20 h-20 rounded-full overflow-hidden bg-[#faf8f5] border border-[#cfc8ba] flex items-center justify-center flex-shrink-0"
        data-testid="photo-preview"
      >
        {cv.photo_data_url ? (
          <img src={cv.photo_data_url} alt="candidate" className="w-full h-full object-cover" />
        ) : (
          <ImagePlus className="text-[#7a6b5d]" size={22} />
        )}
      </div>
      <div>
        <Label className="text-[11px] font-mono-tram tracking-[0.2em] uppercase text-[#7a6b5d]">
          Candidate Photo
        </Label>
        <div className="mt-1 flex items-center gap-2">
          <input
            ref={inp}
            type="file"
            accept="image/*"
            hidden
            data-testid="photo-input"
            onChange={onPick}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-[#cfc8ba]"
            onClick={() => inp.current?.click()}
            data-testid="upload-photo-btn"
          >
            <ImagePlus size={13} className="mr-1" />
            {cv.photo_data_url ? "Replace" : "Upload photo"}
          </Button>
          {cv.photo_data_url && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-[#cfc8ba] text-[#c62828]"
              onClick={() => setCv((c) => ({ ...c, photo_data_url: "" }))}
              data-testid="remove-photo-btn"
            >
              <X size={13} className="mr-1" /> Remove
            </Button>
          )}
        </div>
        <p className="text-[11px] text-[#7a6b5d] mt-1">
          Appears on the right of the navy header band. JPG/PNG, up to 3MB.
        </p>
      </div>
    </div>
  );
};

export default function CvForm({ cv, setCv }) {
  const upd = (patch) => setCv((c) => ({ ...c, ...patch }));

  const updList = (key, i, patch) => {
    setCv((c) => {
      const arr = [...c[key]];
      arr[i] = { ...arr[i], ...patch };
      return { ...c, [key]: arr };
    });
  };
  const addTo = (key, item) => setCv((c) => ({ ...c, [key]: [...c[key], item] }));
  const removeFrom = (key, i) =>
    setCv((c) => ({ ...c, [key]: c[key].filter((_, idx) => idx !== i) }));

  const updBullet = (roleIdx, bIdx, val) => {
    setCv((c) => {
      const roles = [...c.work_experience];
      const bullets = [...roles[roleIdx].bullets];
      bullets[bIdx] = val;
      roles[roleIdx] = { ...roles[roleIdx], bullets };
      return { ...c, work_experience: roles };
    });
  };
  const addBullet = (roleIdx) => {
    setCv((c) => {
      const roles = [...c.work_experience];
      roles[roleIdx] = {
        ...roles[roleIdx],
        bullets: [...(roles[roleIdx].bullets || []), ""],
      };
      return { ...c, work_experience: roles };
    });
  };
  const rmBullet = (roleIdx, bIdx) => {
    setCv((c) => {
      const roles = [...c.work_experience];
      roles[roleIdx] = {
        ...roles[roleIdx],
        bullets: roles[roleIdx].bullets.filter((_, i) => i !== bIdx),
      };
      return { ...c, work_experience: roles };
    });
  };

  return (
    <div className="space-y-4">
      <Section title="Personal · Contact" testid="section-personal">
        <PhotoField cv={cv} setCv={setCv} />
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Full Name"
            data-testid="input-full-name"
            value={cv.full_name}
            onChange={(e) => upd({ full_name: e.target.value })}
          />
          <Field
            label="Title / Role"
            data-testid="input-title"
            value={cv.title}
            onChange={(e) => upd({ title: e.target.value })}
          />
          <Field
            label="Nationality"
            value={cv.nationality}
            onChange={(e) => upd({ nationality: e.target.value })}
          />
          <Field
            label="Location"
            value={cv.location}
            onChange={(e) => upd({ location: e.target.value })}
          />
          <Field label="Age" value={cv.age} onChange={(e) => upd({ age: e.target.value })} />
          <Field
            label="Languages"
            value={cv.languages}
            onChange={(e) => upd({ languages: e.target.value })}
          />
          <Field label="Email" value={cv.email} onChange={(e) => upd({ email: e.target.value })} />
          <Field label="Phone" value={cv.phone} onChange={(e) => upd({ phone: e.target.value })} />
        </div>
        <TArea
          label="Personal Summary"
          data-testid="input-summary"
          value={cv.personal_summary}
          onChange={(e) => upd({ personal_summary: e.target.value })}
        />
      </Section>

      <Section title="Work Experience" testid="section-work">
        {cv.work_experience.map((r, i) => (
          <div key={i} className="border border-[#e2ddd3] rounded-md p-4 bg-[#faf8f5]">
            <div className="flex items-center justify-between mb-3">
              <span className="overline">Role {i + 1}</span>
              <button
                onClick={() => removeFrom("work_experience", i)}
                className="text-[#c62828] hover:opacity-80"
                data-testid={`remove-role-${i}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Job Title"
                value={r.job_title}
                onChange={(e) => updList("work_experience", i, { job_title: e.target.value })}
              />
              <Field
                label="Company"
                value={r.company}
                onChange={(e) => updList("work_experience", i, { company: e.target.value })}
              />
              <Field
                label="Location"
                value={r.location}
                onChange={(e) => updList("work_experience", i, { location: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="Start"
                  value={r.start_date}
                  onChange={(e) => updList("work_experience", i, { start_date: e.target.value })}
                />
                <Field
                  label="End"
                  value={r.end_date}
                  onChange={(e) => updList("work_experience", i, { end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="mt-3 space-y-2">
              <Label className="text-[11px] font-mono-tram tracking-[0.2em] uppercase text-[#7a6b5d]">
                Bullets
              </Label>
              {(r.bullets || []).map((b, bi) => (
                <div key={bi} className="flex items-start gap-2">
                  <Textarea
                    className="bg-white border-[#cfc8ba] min-h-[52px]"
                    value={b}
                    onChange={(e) => updBullet(i, bi, e.target.value)}
                  />
                  <button
                    onClick={() => rmBullet(i, bi)}
                    className="mt-2 text-[#c62828]"
                    data-testid={`rm-bullet-${i}-${bi}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => addBullet(i)}
                data-testid={`add-bullet-${i}`}
                className="border-[#cfc8ba]"
              >
                <Plus size={14} className="mr-1" /> Add bullet
              </Button>
            </div>
          </div>
        ))}
        <Button
          onClick={() => addTo("work_experience", emptyRole())}
          variant="outline"
          className="border-[#cfc8ba]"
          data-testid="add-role"
        >
          <Plus size={14} className="mr-1" /> Add role
        </Button>
      </Section>

      <Section title="Previous Employment" testid="section-prev">
        {cv.previous_employment.map((p, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_140px_auto] gap-2 items-end">
            <Field
              label="Role"
              value={p.role}
              onChange={(e) => updList("previous_employment", i, { role: e.target.value })}
            />
            <Field
              label="Company"
              value={p.company}
              onChange={(e) => updList("previous_employment", i, { company: e.target.value })}
            />
            <Field
              label="Years"
              value={p.years}
              onChange={(e) => updList("previous_employment", i, { years: e.target.value })}
            />
            <button
              onClick={() => removeFrom("previous_employment", i)}
              className="text-[#c62828] pb-2"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <Button
          onClick={() => addTo("previous_employment", emptyPrev())}
          variant="outline"
          className="border-[#cfc8ba]"
        >
          <Plus size={14} className="mr-1" /> Add previous role
        </Button>
      </Section>

      <Section title="Education" testid="section-edu">
        {cv.education.map((e, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_140px_auto] gap-2 items-end">
            <Field
              label="Qualification"
              value={e.qualification}
              onChange={(ev) => updList("education", i, { qualification: ev.target.value })}
            />
            <Field
              label="Institution"
              value={e.institution}
              onChange={(ev) => updList("education", i, { institution: ev.target.value })}
            />
            <Field
              label="Year"
              value={e.year}
              onChange={(ev) => updList("education", i, { year: ev.target.value })}
            />
            <button onClick={() => removeFrom("education", i)} className="text-[#c62828] pb-2">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <Button
          onClick={() => addTo("education", emptyEdu())}
          variant="outline"
          className="border-[#cfc8ba]"
        >
          <Plus size={14} className="mr-1" /> Add education
        </Button>
      </Section>

      <Section title="Certifications" testid="section-certs">
        {cv.certifications.map((c, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <Field
              label="Certification"
              value={c.name}
              onChange={(ev) => updList("certifications", i, { name: ev.target.value })}
            />
            <Field
              label="Issuer / Year"
              value={c.issuer_year}
              onChange={(ev) => updList("certifications", i, { issuer_year: ev.target.value })}
            />
            <button onClick={() => removeFrom("certifications", i)} className="text-[#c62828] pb-2">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <Button
          onClick={() => addTo("certifications", emptyCert())}
          variant="outline"
          className="border-[#cfc8ba]"
        >
          <Plus size={14} className="mr-1" /> Add certification
        </Button>
      </Section>

      <Section title="Core Competencies" testid="section-comp" defaultOpen={false}>
        <TArea
          label="One per line"
          value={(cv.competencies || []).join("\n")}
          onChange={(e) =>
            upd({ competencies: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })
          }
        />
      </Section>

      <Section title="Career Highlights" testid="section-high" defaultOpen={false}>
        <TArea
          label="One per line"
          value={(cv.career_highlights || []).join("\n")}
          onChange={(e) =>
            upd({
              career_highlights: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
            })
          }
        />
      </Section>
    </div>
  );
}
